import { Advertiser, CommercialType, LocationType } from '@talon/web/lib/api';
import { useCommercialStore, useProjectStore, useRoleStore, useSessionStore, useUserStore } from '@talon/web/src/context';
import { Box, Button, Card, Layer, ResponsiveContext, Spinner, Text } from 'grommet';
import { Close } from 'grommet-icons';
import { kebabCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IAddress } from '../../../utils/decodeGoogle';
import { toDateOnly } from 'src/utils/dateOnly';
import { AssetsTab, IRolePerformer, MediaTab, RolePerformersTab, ShootTab, StartTab, SummaryTab,
  defaultRolePerformer, formatDefaultName, validAssetLength } from '.';
import { ProjectWizardFooter } from './ProjectWizardFooter';

const steps = ['Start', 'Shoot', 'Media', 'Assets', 'Roles', 'Review'];
const brand = '#0d48a1';
export type IMediaAssetLengthMap = Partial<Record<CommercialType, number[]>>;
export type ProjectWizardProps = { close: () => void };
type FailureType = 'sessions' | 'locations' | 'commercials' | 'roles';
type Progress = { sessions: number; locations: number; commercials: number; roles: number };
export type WizardIssue = { type: FailureType | 'roleGroups'; label: string };

export const ProjectWizard = observer(({ close }: ProjectWizardProps) => {
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  const sessionStore = useSessionStore();
  const commercialStore = useCommercialStore();
  const roleStore = useRoleStore();
  const navigate = useNavigate();
  const mobile = useContext(ResponsiveContext) === 'small';
  const creatingRef = useRef(false);
  const stoppedRef = useRef(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visited, setVisited] = useState<number[]>([]);
  const [discard, setDiscard] = useState(false);
  const [showStartErrors, setShowStartErrors] = useState(false);
  const [showAssetErrors, setShowAssetErrors] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [progress, setProgress] = useState<Progress>({ sessions: 0, locations: 0, commercials: 0, roles: 0 });
  const [failures, setFailures] = useState<Progress>({ sessions: 0, locations: 0, commercials: 0, roles: 0 });
  const [groupFailures, setGroupFailures] = useState(0);
  const [issues, setIssues] = useState<WizardIssue[]>([]);
  const [running, setRunning] = useState<FailureType | 'project'>();
  const [projectDone, setProjectDone] = useState(false);
  const [union, setUnion] = useState<string>();
  const [geography, setShoot] = useState<string>();
  const [place, setPlace] = useState<IAddress>();
  const [locationType, setLocationType] = useState<LocationType>();
  const [firstSessionDate, setFirstSessionDate] = useState(toDateOnly(new Date()));
  const [code, setCode] = useState('');
  const [media, setMediaState] = useState<CommercialType[]>([]);
  const [assetCounts, setAssetCounts] = useState<IMediaAssetLengthMap>({});
  const [rolePerformers, setRolePerformers] = useState<IRolePerformer[]>([{ ...defaultRolePerformer }]);
  const [exclusivity, setExclusivity] = useState<string>();
  const [clientList, setClientList] = useState<Partial<Advertiser>[]>(projectStore.clientList);
  const [name, setName] = useState('');
  const [client, setClient] = useState<Partial<Advertiser>>();
  const [product, setProduct] = useState('');
  const [budget, setBudget] = useState(0);
  useEffect(() => {
    stoppedRef.current = false;
    return () => { stoppedRef.current = true; };
  }, []);
  useEffect(() => {
    if (projectStore.clientList.length) setClientList(projectStore.clientList);
  }, [projectStore.clientList]);
  const startValid = name.trim().length >= 2 && !!client && product.trim().length >= 1;
  const assetsValid = media.every((medium) => (assetCounts[medium] ?? [30]).every(validAssetLength));
  const isDirty = !!(name || client || product || code || budget || union || geography || place || locationType ||
    firstSessionDate !== toDateOnly(new Date()) ||
    media.length || exclusivity || Object.values(assetCounts).some((lengths) => lengths?.length) ||
    rolePerformers.length !== 1 || rolePerformers.some((role) =>
    role.name !== defaultRolePerformer.name || role.roleType !== defaultRolePerformer.roleType ||
      role.performerType !== defaultRolePerformer.performerType || role.groupSize !== defaultRolePerformer.groupSize));
  const mediaOptions = Object.values(CommercialType);
  const commercialsTotal = media.reduce((sum, medium) => sum + (assetCounts[medium] ?? [30]).length, 0);
  const rolesTotal = rolePerformers.reduce((sum, role) => sum + role.groupSize, 0);

  const setMedia = (next: CommercialType[]) => {
    setMediaState(next);
    setAssetCounts((current) => Object.fromEntries(
      next.map((medium) => [medium, current[medium] ?? [30]])
    ) as IMediaAssetLengthMap);
  };
  const closeWizard = () => {
    if (creatingRef.current) return;
    if (isDirty) setDiscard(true);
    else close();
  };
  const goTo = (index: number) => {
    if (creatingRef.current || index < 0 || index >= steps.length) return;
    if (index > 0 && !startValid) {
      setActiveSlide(0);
      setShowStartErrors(true);
      requestAnimationFrame(() => document.getElementById(
        name.trim().length < 2 ? 'project-name' : !client ? 'select-client' : 'product'
      )?.focus());
      return;
    }
    if (index > 3 && !assetsValid) {
      setActiveSlide(3);
      setShowAssetErrors(true);
      return;
    }
    setVisited((current) => current.includes(activeSlide) ? current : [...current, activeSlide]);
    setActiveSlide(index);
  };
  const next = () => goTo(activeSlide + 1);
  const continueToProject = () => {
    stoppedRef.current = true;
    projectStore.setCurrentProjectName(name.trim());
    navigate(`/project/${kebabCase(name.trim())}`, {
      state: { wizardIssues: issues, wizardUncertain: true },
    });
  };

  const createProject = async () => {
    if (creatingRef.current) return;
    if (!startValid) { goTo(0); setShowStartErrors(true); return; }
    if (!assetsValid) { setActiveSlide(3); setShowAssetErrors(true); return; }
    if (!userStore.user.active_group) {
      setCreateError('Couldn\'t create the project. Nothing was saved. Try again.');
      return;
    }
    creatingRef.current = true;
    stoppedRef.current = false;
    setCreating(true);
    setRunning('project');
    setCreateError('');
    let projectClient: Partial<Advertiser> | undefined = client;
    const creatingClient = client?.id === 'NEW';
    let project: Awaited<ReturnType<typeof projectStore.createProject>> = undefined;
    try {
      if (creatingClient) {
        projectClient = await projectStore.createClient({
          name: client?.name || '', isSagSignatory: !!client?.isSagSignatory, isJpcAuthorizer: !!client?.isJpcAuthorizer,
        });
      }
      if (stoppedRef.current) return;
      if (projectClient?.id) {
        project = await projectStore.createProject({
          name: name.trim(), groupId: userStore.user.active_group.id, clientId: projectClient.id,
          product: product.trim(), jobCode: code, is_union: union === 'union',
          talentBudgetInPennies: budget ? budget * 100 : 0,
        });
      }
    } catch {
      // A lost response does not prove whether the server saved the client or project.
    }
    if (stoppedRef.current) return;
    if (project === undefined) {
      if (projectClient?.id && creatingClient) setClient(projectClient);
      setCreating(false);
      creatingRef.current = false;
      setRunning(undefined);
      setCreateError('Couldn\'t confirm whether the project was created. Check Projects before trying again.');
      return;
    }
    if (project === null) {
      const removedClient = creatingClient && projectClient?.id ?
        await projectStore.deleteClient(projectClient.id) : true;
      if (!removedClient) setClient(projectClient);
      setCreating(false);
      creatingRef.current = false;
      setRunning(undefined);
      setCreateError(removedClient ? 'Couldn\'t create the project. Nothing was saved. Try again.' :
        'Couldn\'t create the project. The new client was saved. Try again.');
      return;
    }
    setProjectDone(true);
    const failed: Progress = { sessions: 0, locations: 0, commercials: 0, roles: 0 };
    const done: Progress = { sessions: 0, locations: 0, commercials: 0, roles: 0 };
    const creationIssues: WizardIssue[] = [];
    const record = (type: FailureType, success: boolean, label: string) => {
      if (stoppedRef.current) return;
      if (success) done[type]++;
      else {
        failed[type]++;
        creationIssues.push({ type, label });
        setIssues([...creationIssues]);
      }
      setProgress({ ...done });
      setFailures({ ...failed });
    };
    if (place) {
      if (stoppedRef.current) return;
      setRunning('locations');
      try {
        const location = await projectStore.createLocation({
          name: [place.city, place.state].filter(Boolean).join(', ') || 'Shoot location',
          type: locationType || LocationType.Nearby, street_address: place.street_address || '',
          aptOrUnit: place.aptOrUnit || '', city: place.city || '', state: place.state || '',
          zip: place.zip || '', lat: place.lat || 0, lng: place.lng || 0, projectId: project.id,
        });
        record('locations', !!location, 'Shoot location');
      } catch { record('locations', false, 'Shoot location'); }
    }
    if (firstSessionDate) {
      if (stoppedRef.current) return;
      setRunning('sessions');
      try {
        // Of the project's first medium (radio, for a radio project), so its roles are offered
        const session = await sessionStore.createSession(
          new Date(firstSessionDate).toISOString(), project.id, undefined, media[0]);
        record('sessions', !!session, 'First session');
      } catch { record('sessions', false, 'First session'); }
    }
    for (const medium of media) {
      for (const length of assetCounts[medium] ?? [30]) {
        if (stoppedRef.current) return;
        setRunning('commercials');
        try {
          const commercial = await commercialStore.createCommercial({
            projectId: project.id, title: `${medium} · ${length}s`, type: medium,
            durationInSeconds: length, jobCode: code,
          });
          record('commercials', !!commercial, `${medium} · ${length}s`);
        } catch { record('commercials', false, `${medium} · ${length}s`); }
      }
    }
    for (const rolePerformer of rolePerformers) {
      if (stoppedRef.current) return;
      const roleIds: string[] = [];
      for (let i = 1; i <= rolePerformer.groupSize; i++) {
        if (stoppedRef.current) return;
        setRunning('roles');
        try {
          const role = await roleStore.createRole({
            name: formatDefaultName(rolePerformer, i), type: rolePerformer.roleType,
            performerType: rolePerformer.performerType, projectId: project.id,
          });
          if (role) roleIds.push(role.id);
          record('roles', !!role, formatDefaultName(rolePerformer, i));
        } catch { record('roles', false, formatDefaultName(rolePerformer, i)); }
      }
      if (rolePerformer.groupSize > 1 && roleIds.length > 0) {
        if (stoppedRef.current) return;
        setRunning('roles');
        try {
          const group = await roleStore.createRoleGroup({
            name: formatDefaultName(rolePerformer), roleIds, projectId: project.id,
          });
          if (!group) throw new Error('Role group creation failed');
        } catch {
          if (stoppedRef.current) return;
          creationIssues.push({ type: 'roleGroups', label: formatDefaultName(rolePerformer) });
          setIssues([...creationIssues]);
          setGroupFailures((current) => current + 1);
        }
        if (stoppedRef.current) return;
      }
    }
    if (stoppedRef.current) return;
    setRunning(undefined);
    projectStore.setCurrentProjectName(name.trim());
    if (creationIssues.length) toast.info(`${name.trim()} created with items to review`, { autoClose: 3000 });
    else toast.success(`${name.trim()} created`, { autoClose: 3000 });
    navigate(`/project/${kebabCase(name.trim())}`, { state: { wizardIssues: creationIssues } });
  };

  const progressRows: { label: string; type?: FailureType; total?: number }[] = [
    { label: 'Project' },
    ...(firstSessionDate ? [{ label: 'First session', type: 'sessions' as const, total: 1 }] : []),
    ...(place ? [{ label: 'Location', type: 'locations' as const, total: 1 }] : []),
    { label: 'Commercials', type: 'commercials', total: commercialsTotal },
    { label: 'Roles', type: 'roles', total: rolesTotal },
  ];

  return (
    <Layer onEsc={closeWizard} position="center" modal background="transparent" full={mobile}>
      <Card width={mobile ? '100%' : 'large'} elevation="medium"
        style={{ height: mobile ? '100dvh' : 'min(90vh, 760px)', borderRadius: mobile ? 0 : 12, overflow: 'hidden' }}>
        <Box flex={false} background="white" border={{ side: 'bottom', color: '#E3E3E3' }}
          pad={{ horizontal: '16px', vertical: '12px' }} direction="row" justify="between" align="center">
          <Box gap="xsmall">
            <Text size="18px" weight={600} color="#222">New project</Text>
            <Text size="13px" color="#666">Step {activeSlide + 1} of 6 · {steps[activeSlide]}</Text>
          </Box>
          <Button icon={<Close size="medium" />} onClick={closeWizard} disabled={creating} a11yTitle="Close" />
        </Box>
        {mobile ? <Box flex={false} height="4px" background="#E3E3E3">
          <Box width={`${(activeSlide + 1) * 100 / 6}%`} height="4px" background={brand} round="2px" />
        </Box> : <Box flex={false} direction="row" align="center" pad="medium" background="white">
          {steps.map((step, index) => <Box key={step} direction="row" align="center" flex={index < 5}>
            <Button plain onClick={() => goTo(index)} disabled={creating}
              aria-current={activeSlide === index ? 'step' : undefined}
              aria-disabled={!startValid && index > 0} title={!startValid && index > 0 ? 'Finish Start first' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: activeSlide === index ? brand : '#777' }}>
              <Box width="22px" height="22px" round="full" align="center" justify="center"
                background={visited.includes(index) && activeSlide !== index ? brand : 'white'}
                border={visited.includes(index) && activeSlide !== index ? undefined :
                  { color: activeSlide === index ? brand : '#BBB', size: activeSlide === index ? '2px' : '1px' }}>
                <Text size="12px" color={visited.includes(index) && activeSlide !== index ? 'white' : 'inherit'}>
                  {visited.includes(index) && activeSlide !== index ? '✓' : index + 1}
                </Text>
              </Box>
              <Text size="13px" weight={activeSlide === index ? 600 : 400}>{step}</Text>
            </Button>
            {index < 5 && <Box flex border={{ side: 'bottom', color: visited.includes(index) ? brand : '#DADADA' }}
              margin={{ horizontal: '8px' }} />}
          </Box>)}
        </Box>}
        <Box flex overflow={{ vertical: 'auto' }} background="white">
          {activeSlide === 0 && <StartTab name={name} code={code} client={client} product={product} budget={budget}
            clientList={clientList} setName={setName} setCode={setCode} setClient={setClient}
            setClientList={setClientList} setProduct={setProduct} setBudget={setBudget} showErrors={showStartErrors} />}
          {activeSlide === 1 && <ShootTab geography={geography} place={place} setPlace={setPlace} union={union}
            firstSessionDate={firstSessionDate} locationType={locationType} setFirstSessionDate={setFirstSessionDate}
            setShoot={setShoot} setUnion={setUnion} setLocationType={setLocationType} />}
          {activeSlide === 2 && <MediaTab media={media} setMedia={setMedia} mediaOptions={mediaOptions}
            exclusivity={exclusivity} setExclusivity={setExclusivity} />}
          {activeSlide === 3 && <AssetsTab media={media} setAssetCounts={setAssetCounts} assetCounts={assetCounts}
            showErrors={showAssetErrors} onMedia={() => goTo(2)} />}
          {activeSlide === 4 && <RolePerformersTab setRolePerformers={setRolePerformers}
            rolePerformers={rolePerformers} media={media} />}
          {activeSlide === 5 && <Box>
            {creating ? <Box pad="medium" gap="small">
              {progressRows.map(({ label, type, total }) => {
                const count = type ? progress[type] : projectDone ? 1 : 0;
                const failed = type ? failures[type] + (type === 'roles' ? groupFailures : 0) : 0;
                const done = !failed && count === (total ?? 1);
                return <Box key={label} direction="row" align="center" gap="small">
                  <Box width="20px" height="20px" round="full" align="center" justify="center"
                    border={done || failed ? undefined : { color: running === (type ?? 'project') ? brand : '#CCC' }}
                    background={done && !failed ? brand : 'white'}>
                    {failed ? <Text color="#c62828">×</Text> : done ? <Text color="white" size="12px">✓</Text> :
                      running === (type ?? 'project') ? <Spinner size="small" /> : null}
                  </Box>
                  <Text size="14px" weight={running === (type ?? 'project') ? 600 : 400}
                    color={failed ? '#c62828' : running === (type ?? 'project') ? brand : done ? '#333' : '#999'}>{label}
                    {type && total && total > 1 ? ` ${count} of ${total}` : ''}
                    {failed ? ` · ${failed} failed` : ''}</Text>
                </Box>;
              })}
              {projectDone && <Box gap="xsmall" margin={{ top: 'small' }}>
                <Button secondary label="Continue to project" onClick={continueToProject} alignSelf="start" />
                <Text size="12px" color="#666">
                  An item may still finish saving. Check the project before adding it again.
                </Text>
              </Box>}
            </Box> : <SummaryTab name={name} code={code} client={client} product={product} budget={budget}
              union={union} geography={geography} place={place} media={media} exclusivity={exclusivity}
              assetCounts={assetCounts} rolePerformers={rolePerformers} firstSessionDate={firstSessionDate}
              locationType={locationType} onEdit={goTo} />}
            {createError && <Box margin="medium" pad="small" background="#fff0f0" round="small">
              <Text color="#c62828" size="14px">{createError}</Text>
            </Box>}
          </Box>}
        </Box>
        <ProjectWizardFooter activeSlide={activeSlide} onBack={() => goTo(activeSlide - 1)}
          onNext={next} onSkip={next} onCreate={createProject} creating={creating} />
      </Card>
      {discard && <Layer onEsc={() => setDiscard(false)}>
        <Box pad="medium" gap="medium" width="medium">
          <Text size="18px" weight={600}>Discard this project?</Text>
          <Text>What you've entered so far won't be saved.</Text>
          <Box direction="row" justify="end" gap="small">
            <Button label="Keep editing" onClick={() => setDiscard(false)} />
            <Button primary color="#c62828" label="Discard" onClick={close} />
          </Box>
        </Box>
      </Layer>}
    </Layer>
  );
});
